import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock, Power, PowerOff, ToggleLeft, ToggleRight } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, post } from '../../nucleo/api';
import { data } from '../../nucleo/formato';
import { rotuloModulo } from '../../nucleo/rotulos';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import type { Lista, ModuloAdmin, ResultadoInstalacao } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

/** Dependências transitivas que ainda não estão instaladas (o que "instalar" vai puxar junto). */
function faltantes(alvo: ModuloAdmin, todos: ModuloAdmin[]): ModuloAdmin[] {
  const porSlug = new Map(todos.map((m) => [m.slug, m]));
  const vistos = new Set<string>();
  const pilha = [...alvo.depende_de];
  while (pilha.length) {
    const slug = pilha.pop()!;
    if (vistos.has(slug)) continue;
    vistos.add(slug);
    pilha.push(...(porSlug.get(slug)?.depende_de ?? []));
  }
  return [...vistos].map((s) => porSlug.get(s)).filter((m): m is ModuloAdmin => !!m && !m.instalado);
}

/** M12.2: catálogo com estado, versão, dependências e dependentes; instalar e desinstalar sem deploy. */
export function Modulos() {
  const { t } = useTranslation('admin');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [instalar, setInstalar] = useState<ModuloAdmin | null>(null);
  const [desinstalar, setDesinstalar] = useState<ModuloAdmin | null>(null);
  const [alternar, setAlternar] = useState<ModuloAdmin | null>(null);
  const q = useQuery({ queryKey: ['/admin/modulos'], queryFn: () => get<Lista<ModuloAdmin>>('/admin/modulos') });
  const podeEditar = s.pode('admin.modulos');
  // Pacote 2: ligar no workspace vale para todas as empresas dele (r45: só quem vê todas).
  const podeLigar = podeNoWorkspace(s, 'admin.modulos');

  // Menus, abas e permissões da sessão mudam com os módulos instalados.
  const atualizar = async () => {
    await qc.invalidateQueries({ queryKey: ['/admin/modulos'] });
    await s.recarregar();
  };

  const mInstalar = useMutation({
    mutationFn: (m: ModuloAdmin) => post<ResultadoInstalacao>(`/admin/modulos/${m.slug}/instalar`),
    onSuccess: async (r) => {
      setInstalar(null);
      const nomes = new Map((q.data?.itens ?? []).map((x) => [x.slug, x.nome]));
      toast.sucesso(r.instalados.length ? t('modulos.okInstalado', { lista: r.instalados.map((x) => rotuloModulo(x, nomes.get(x))).join(', ') }) : t('modulos.okJaInstalado'));
      await atualizar();
    },
    onError: (e) => {
      setInstalar(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const mDesinstalar = useMutation({
    mutationFn: (m: ModuloAdmin) => post(`/admin/modulos/${m.slug}/desinstalar`),
    onSuccess: async (_r, m) => {
      setDesinstalar(null);
      toast.sucesso(t('modulos.okDesinstalado', { nome: rotuloModulo(m.slug, m.nome) }));
      await atualizar();
    },
    onError: (e) => {
      setDesinstalar(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const mAlternar = useMutation({
    mutationFn: (m: ModuloAdmin) => api('PUT', `/admin/modulos/${m.slug}/workspace`, { ligado: !m.ligado_no_workspace }),
    onSuccess: async (_r, m) => {
      setAlternar(null);
      toast.sucesso(t(m.ligado_no_workspace ? 'modulos.okDesligado' : 'modulos.okLigado', { nome: rotuloModulo(m.slug, m.nome) }));
      await atualizar();
    },
    onError: (e) => {
      setAlternar(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={t('modulos.vazio')} />
          ) : (
          <ul className="grid gap-3 lg:grid-cols-2">
            {d.itens.map((m) => {
              const bloqueado = m.dependentes_instalados.length > 0;
              return (
                <li key={m.slug} className="flex min-w-0 flex-col gap-3 rounded-xl border border-borda bg-sup-1 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <TextoCortado como="h2" className="text-base font-semibold">{rotuloModulo(m.slug, m.nome)}</TextoCortado>
                      <p className="numero mt-0.5 text-xs text-tinta-3">
                        {t('modulos.versao', { v: m.versao })}
                      </p>
                    </div>
                    <span className="flex flex-wrap items-center gap-1.5">
                      {m.fixo && (
                        <Selo cor="dourado">
                          <Lock className="mr-1 size-3" aria-hidden />
                          {t('modulos.fixo')}
                        </Selo>
                      )}
                      <Selo cor={m.instalado ? 'verde' : 'neutro'}>{m.instalado ? t('modulos.instalado') : t('modulos.naoInstalado')}</Selo>
                      {m.liberado_por_workspace && m.instalado && (
                        <Selo cor={m.ligado_no_workspace ? 'verde' : 'laranja'}>{m.ligado_no_workspace ? t('modulos.ligadoNoWorkspace') : t('modulos.desligadoNoWorkspace')}</Selo>
                      )}
                    </span>
                  </div>

                  <dl className="grid gap-1.5 text-sm">
                    <div className="flex flex-wrap gap-x-2">
                      <dt className="text-tinta-3">{t('modulos.dependeDe')}:</dt>
                      <dd>{m.depende_de_nomes.length ? m.depende_de_nomes.map((x) => rotuloModulo(x.slug, x.nome)).join(', ') : t('modulos.nenhum')}</dd>
                    </div>
                    {!!m.opcional_de_nomes?.length && (
                      <div className="flex flex-wrap gap-x-2">
                        <dt className="text-tinta-3">{t('modulos.usaSeInstalado')}:</dt>
                        <dd>{m.opcional_de_nomes.map((x) => rotuloModulo(x.slug, x.nome)).join(', ')}</dd>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-2">
                      <dt className="text-tinta-3">{t('modulos.dependentes')}:</dt>
                      <dd>{m.dependentes.length ? m.dependentes.map((x) => rotuloModulo(x)).join(', ') : t('modulos.nenhum')}</dd>
                    </div>
                    {m.liberado_por_workspace && (
                      <div className="flex flex-wrap gap-x-2">
                        <dt className="text-tinta-3">{t('modulos.porWorkspace')}:</dt>
                        <dd>{t('modulos.porWorkspaceAjuda')}</dd>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-2 text-xs text-tinta-3">
                      <dd>{t('modulos.permissoes', { count: m.permissoes.length })}</dd>
                      {m.instalado_em && m.instalado && <dd>{t('modulos.instaladoEm', { data: data(m.instalado_em) })}</dd>}
                    </div>
                  </dl>

                  {podeEditar && (
                    <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-borda pt-3">
                      <p className="min-w-0 flex-1 text-xs text-tinta-3">{m.fixo ? t('modulos.fixoAviso') : bloqueado && m.instalado ? t('modulos.bloqueado', { lista: m.dependentes_instalados.map((x) => rotuloModulo(x.slug, x.nome)).join(', ') }) : ''}</p>
                      {m.liberado_por_workspace && m.instalado && podeLigar && (
                        <Botao icone={m.ligado_no_workspace ? <ToggleLeft className="size-4" /> : <ToggleRight className="size-4" />} onClick={() => setAlternar(m)}>
                          {m.ligado_no_workspace ? t('modulos.desligarNoWorkspace') : t('modulos.ligarNoWorkspace')}
                        </Botao>
                      )}
                      {m.instalado ? (
                        <Botao variante="perigo" icone={<PowerOff className="size-4" />} disabled={m.fixo || bloqueado} onClick={() => setDesinstalar(m)}>
                          {t('modulos.desinstalar')}
                        </Botao>
                      ) : (
                        <Botao variante="primario" icone={<Power className="size-4" />} onClick={() => setInstalar(m)}>
                          {t('modulos.instalar')}
                        </Botao>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          )
        }
      </Estado>

      <Confirmar
        aberto={!!instalar}
        aoFechar={() => setInstalar(null)}
        aoConfirmar={() => instalar && mInstalar.mutate(instalar)}
        carregando={mInstalar.isPending}
        titulo={t('modulos.confirmarInstalar', { nome: instalar ? rotuloModulo(instalar.slug, instalar.nome) : '' })}
        texto={(() => {
          const extra = instalar && q.data ? faltantes(instalar, q.data.itens) : [];
          return extra.length ? t('modulos.instalarTambem', { lista: extra.map((x) => rotuloModulo(x.slug, x.nome)).join(', ') }) : t('modulos.instalarSem');
        })()}
      />
      <Confirmar
        aberto={!!desinstalar}
        aoFechar={() => setDesinstalar(null)}
        aoConfirmar={() => desinstalar && mDesinstalar.mutate(desinstalar)}
        carregando={mDesinstalar.isPending}
        perigoso
        titulo={t('modulos.confirmarDesinstalar', { nome: desinstalar ? rotuloModulo(desinstalar.slug, desinstalar.nome) : '' })}
        texto={t('modulos.desinstalarTexto')}
      />
      <Confirmar
        aberto={!!alternar}
        aoFechar={() => setAlternar(null)}
        aoConfirmar={() => alternar && mAlternar.mutate(alternar)}
        carregando={mAlternar.isPending}
        perigoso={!!alternar?.ligado_no_workspace}
        titulo={t(alternar?.ligado_no_workspace ? 'modulos.confirmarDesligar' : 'modulos.confirmarLigar', { nome: alternar ? rotuloModulo(alternar.slug, alternar.nome) : '' })}
        texto={t(alternar?.ligado_no_workspace ? 'modulos.desligarTexto' : 'modulos.ligarTexto', { workspace: s.workspace?.nome ?? '' })}
      />
    </>
  );
}
